// types/promotion.ts

export interface LocalizedText {
  title: string
  bannerTag: string
  productTag: string
  description: string
  excerpt: string
}

export enum ConditionType {
  BUY = 'BUY',
}

export enum ItemType {
  ANY = 'ANY',
  PRODUCT_ID = 'PRODUCT_ID',
  PRODUCT_LINE = 'PRODUCT_LINE',
  PRODUCT_CONTAINER = 'PRODUCT_CONTAINER',
  PRODUCT_GROUP = 'PRODUCT_GROUP',
  PRODUCT_SIZE = 'PRODUCT_SIZE',
  CATEGORY = 'CATEGORY',
  SUBCATEGORY = 'SUBCATEGORY',
  BRAND = 'BRAND',
  SUBBRAND = 'SUBBRAND',
  GTIN = 'GTIN',
}

export enum LogicalOperator {
  AND = 'AND',
  OR = 'OR',
}

export type AnyOptionType = 'ANY'

export interface ExcludedItem {
  id: string
  displayText: string
}

export interface SelectedItem {
  id: string
  displayText: string
}

export interface Condition {
  type: ConditionType
  // We need to use string type for quantity to accomodate 'Any' value
  quantity?: number | null
  itemType: ItemType
  items: Item[]
  productCombination?: boolean // Optional, default false
}

export interface ConditionGroup {
  combinedWith?: LogicalOperator // Optional, default is AND
  conditions: Condition[]
}

export interface ConditionGroup {
  conditions: Condition[]
}

export interface PromotionCondition {
  sequence: number
  conditionGroup?: ConditionGroup
  rewards?: Reward[]
}

export enum SegmentType {
  ALL_CUSTOMERS = 'ALL_CUSTOMERS',
  SELECTED_GROUPS = 'SELECTED_GROUPS',
  SELECTED_SEGMENTS = 'SELECTED_SEGMENTS',
}

export interface SegmentGroup {
  description?: string
  segments: string[]
  segmentFolders: string[]
}

export interface SegmentConfiguration {
  selectedSegments: SelectedSegmentAPIGroup[]
  excludedSegments: ExcludedSegmentsAPI
}

/**
 * API ready structure for each selected segment display group.
 */
export interface SelectedSegmentAPIGroup {
  segments?: string[]
  segmentsView?: Array<{
    segmentId: string
    segmentGroupId: string
    segmentName?: string
  }>
  displayGroupId: string
  selectionType: SegmentType
}

/**
 * API ready structure for excluded segments.
 */
export interface ExcludedSegmentsAPI {
  segments: string[]
  segmentsView: Array<{
    segmentId: string
    segmentGroupId: string
    segmentName?: string
  }>
  selectionType: 'SELECTED_SEGMENTS' | 'SELECTED_GROUPS'
}

export enum RewardType {
  FREE_PRODUCT = 'FREE_PRODUCT',
  POINTS = 'POINTS',
  DISCOUNT = 'DISCOUNT',
  GIFT_ITEM = 'GIFT_ITEM',
}

export interface FreeProductReward {
  type: RewardType.FREE_PRODUCT
  quantity: number
  items: string[]
  itemType: ItemType
}

export interface PointsReward {
  type: RewardType.POINTS
  points: number
}

export interface DiscountReward {
  type: RewardType.DISCOUNT
  discountType: string
  discountValue: number
}

export interface GiftItemReward {
  type: RewardType.GIFT_ITEM
  quantity: number
  items: string[]
}

/**
 * Union type that resolves based on the `type` discriminator.
 */
export type Reward = FreeProductReward | PointsReward | DiscountReward | GiftItemReward

export enum PromotionType {
  BUY_SINGLE_PRODUCT = 'BUY_SINGLE_PRODUCT',
  BUY_MULTIPLE_PRODUCTS = 'BUY_MULTIPLE_PRODUCTS',
  STORE_DISCOUNT = 'STORE_DISCOUNT',
  WELCOME_DISCOUNT = 'WELCOME_DISCOUNT',
}

export enum DisplayPage {
  HOME_PAGE = 'HOME_PAGE',
  PRODUCT_PAGE = 'PRODUCT_PAGE',
  PRODUCT_CARD = 'PRODUCT_CARD',
  PRODUCT_CATALOGUE = 'PRODUCT_CATALOGUE',
}

export enum PromotionStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
}

export interface PromotionText {
  [locale: string]: LocalizedText
}

export interface SelectedSegmentGroup {
  groupId: string
  groupName: string
  segments: Array<{
    segmentId: string
    segmentName: string
  }>
}

export interface Item {
  id: string
  name: string
}

export interface ExcludedItemsType {
  /** The itemType refers to the ItemType selected in the UI (e.g. PRODUCT_ID) */
  itemType: string
  /** List of item identifiers being excluded */
  items: Item[]
}

export interface Promotion {
  id?: string
  name: string
  promotionType: PromotionType
  excludedItems: ExcludedItemsType[]

  promotionConditions: PromotionCondition[]
  segments: SegmentConfiguration
  text: PromotionText
  bannerUrl: string
  displayOn: DisplayPage[]
  displayOrder: number
  activeStartDate: string // ISO 8601 date string
  activeEndDate: string // ISO 8601 date string
  active: boolean
  maxAllowedOrderPerStore: number
  maxFreeItemsPerOrder?: number
  maxTimesPerOrder?: number
  maxAllowedPercentage?: number
  resetFrequencyInDays: number
  bannerType?: string
  landingPage?: string
  behavior: string
  status: PromotionStatus
  routeTypeId?: string
}

export enum PromotionStep {
  SETUP = 1,
  DISPLAY = 2,
  ATTRIBUTES = 3,
  REVIEW = 4,
}
